import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/sharp-solid-svg-icons';
import { useRouter } from 'next/navigation';
import React, { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { ButtonSize } from '@/components/button';
import DropDownButton from '@/components/dropDownButton';
import { CUSTOMIZE_LAND_PLAT_MAP_URL } from '@/constants/urls';
import useBreakpoints, { BreakpointsEnum } from '@/hooks/useBreakpoint';
import { SavedSelection } from '@/models/land';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/auth-provider';
import { useFileManagementContext } from '@/providers/FileManagementProvider';

import { LockToggle } from '../../lockedFile/lockToggle';
import { useNavigationContext } from '../provider';

interface IProps {
  file: SavedSelection;
  className?: string;
}
const FileOverflow = ({ file, className = '' }: IProps) => {
  const { fileManagementUtilities, setActiveFile, closeActiveFile } =
    useFileManagementContext();
  const { breakpoint } = useBreakpoints();
  const isMobile = breakpoint.down(BreakpointsEnum.SM);
  const router = useRouter();
  const {
    userCanAccessPortfolio,
    userDjangoId,
    userCanExportCSV,
    userCanExportKML,
    userCanDuplicateSavedMaps,
  } = useAuth();

  const { aerialMapIsOpen } = useNavigationContext();

  const isFileLocked = useMemo(() => {
    return !fileManagementUtilities?.canUserEditFile?.(file);
  }, [file, fileManagementUtilities]);

  const disableTextForDuplicate = useMemo(() => {
    if (!userCanAccessPortfolio) {
      return 'Upgrade to access';
    }

    return '';
  }, [userCanAccessPortfolio]);

  const { track } = useAnalytics();

  const menuItems = useMemo(() => {
    const initialMenuItems = [
      {
        disabled: isFileLocked,
        id: 'rename',
        label: 'Rename',
        onClick: () => fileManagementUtilities?.renameFile?.(file),
        value: 'rename',
      },
      {
        disabled: !userCanDuplicateSavedMaps,
        disabledText: disableTextForDuplicate,
        id: 'duplicate',
        label: 'Duplicate',
        onClick: () => fileManagementUtilities?.showDuplicateFileModal?.(file),
        value: 'duplicate',
      },
      {
        disabled: isFileLocked,
        id: 'upload',
        label: `Files (${file?.file_count || '0'})`,
        onClick: () => fileManagementUtilities?.uploadFile?.(file),
        value: 'upload',
      },
      {
        disabled: isFileLocked,
        id: 'customize',
        label: 'Customize',
        onClick: () => {
          router.push(`${CUSTOMIZE_LAND_PLAT_MAP_URL}?saveId=${file?.id}`);
          track({
            data: {},
            event: TrackingEvent.CUSTOMIZE_OVERFLOW_MENU_ACTIVE_SAVE,
          });
        },
        value: 'customize',
      },
      {
        disabled: !userCanExportCSV,
        disabledText: 'Upgrade to access',
        id: 'export-csv',
        label: 'Export CSV',
        onClick: () => fileManagementUtilities?.downloadCSV?.(file),
        value: 'exportCSV',
      },
      {
        disabled: !userCanExportKML,
        disabledText: 'Upgrade to access',
        id: 'kml',
        label: 'Export KML',
        onClick: () => fileManagementUtilities?.downloadKML?.(file),
      },
      {
        disabled: isFileLocked,
        id: 'delete',
        label: 'Delete',
        onClick: () => fileManagementUtilities?.deleteFile?.(file),
        value: 'delete',
      },
    ];

    if (isMobile)
      initialMenuItems.push({
        disabled: false,
        id: 'close',
        label: 'Close',
        onClick: () => closeActiveFile?.(),
        value: 'close',
      });

    return initialMenuItems;
  }, [
    isFileLocked,
    userCanExportCSV,
    userCanExportKML,
    userCanDuplicateSavedMaps,
    disableTextForDuplicate,
    file,
    isMobile,
    fileManagementUtilities,
    router,
    track,
    closeActiveFile,
  ]);

  const otherMenuItems = useMemo(() => {
    if (file?.source_user.id === userDjangoId && userCanAccessPortfolio) {
      return [
        {
          customItem: (
            <LockToggle showOnOffText={false} onSaveCallback={setActiveFile} />
          ),
          id: 'toggle',
          label: 'Toggle',
          value: 'toggle',
        },
      ];
    }
    return [];
  }, [
    file?.source_user.id,
    userCanAccessPortfolio,
    setActiveFile,
    userDjangoId,
  ]);

  const groupOptions = useMemo(() => {
    return [menuItems, otherMenuItems].filter((g) => g.length > 0);
  }, [menuItems, otherMenuItems]);

  return (
    <DropDownButton
      buttonClassName={twMerge('w-full h-full text-gray-100', className)}
      buttonSize={ButtonSize.SMALL}
      className="h-8 w-8"
      disabled={aerialMapIsOpen}
      groupOptions={groupOptions}
      overflowClassName="w-[160px]"
      overflowItemClassName="min-w-[100%]"
      placement="bottom-start"
      variant="dark"
    >
      <FontAwesomeIcon icon={faEllipsisVertical} />
    </DropDownButton>
  );
};

export default FileOverflow;
